import React, { forwardRef } from "react";

/**
 * The text field behind every admin `<DatePicker>`. Its whole job is to stop
 * the on-screen keyboard opening without stopping the calendar.
 *
 * react-datepicker's own `readOnly` **prop** cannot be used for this: it also
 * suppresses the component's open handler, so the field goes inert and tapping
 * it does nothing. Setting `readOnly` on the DOM input instead is invisible to
 * react-datepicker — it still receives the click and focus it opens on, while
 * the browser refuses to raise a keyboard for a read-only field.
 *
 * `inputMode="none"` is the modern signal for the same thing and is what
 * current iOS and Android honour; `readOnly` covers older iOS, which ignores
 * it. Both are set deliberately: a keyboard covering the calendar is the bug
 * being fixed, so it is worth two overlapping guards.
 *
 * Applied on every screen size, not just mobile. Dates on these filters are
 * only ever chosen from the calendar, so nothing is lost by making the field
 * non-typable on desktop too, and one code path is easier to reason about than
 * a viewport-dependent one.
 *
 * Props are spread first so react-datepicker's own handlers, `value`,
 * `placeholder` and the `className` each call site passes all survive; the two
 * attributes that matter here are set afterwards so nothing can override them.
 */
const DatePickerInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => (
  <input {...props} ref={ref} readOnly inputMode="none" />
));

DatePickerInput.displayName = "DatePickerInput";

export default DatePickerInput;
